//----------------------------------
// Imports
//----------------------------------
import Vue from 'vue';
import i18n from '@/i18n';
import config from './config';

//----------------------------------
// Constants
//----------------------------------
const { LANGUAGES } = config;
const doc = document;
const head = doc.querySelector('head');
const select = head.querySelector.bind(head);
const selectAll = head.querySelectorAll.bind(head);
const create = doc.createElement.bind(doc);
const vm = new Vue({ data: {
  documentTitle: null,
  title: null,
  description: null,
  image: null,
  url: null,
} });

const internal = {
  updateMetatags: (definition, value) => {
    const props = Object.keys(definition);
    const selector = props.map(prop => `meta[${prop}="${definition[prop]}"]`).join(',');
    let metas = selectAll(selector);
    if (metas.length === 0) {
      props.forEach((prop) => {
        const el = create('meta');
        el.setAttribute(prop, definition[prop]);
        head.appendChild(el);
      });
      metas = selectAll(selector);
    }
    metas.forEach(m => m.setAttribute('content', value));
  },
};

const meta = {
  update: (props, to) => {
    if (props) {
      // update title tag and meta tags
      if (props.documentTitle) { meta.documentTitle = props.documentTitle; }
      if (props.title) { meta.title = props.title; }
      if (props.description) { meta.description = props.description; }
      if (props.image) { meta.image = props.image; }
    }
    // update alternates, canonical, og:url meta tags
    meta.url = to.path;
  },
};

Object.defineProperty(meta, 'documentTitle', {
  get() { return vm.documentTitle; },
  set(value) {
    vm.documentTitle = value;
    let title = select('title');
    if (!title) {
      title = create('title');
      head.appendChild(title);
    }
    title.textContent = value;
  },
});

Object.defineProperty(meta, 'title', {
  get() { return vm.title; },
  set(value) {
    vm.title = value;
    internal.updateMetatags({
      name: 'title',
      itemprop: 'name',
      property: 'og:title',
    }, value);
  },
});

Object.defineProperty(meta, 'description', {
  get() { return vm.description; },
  set(value) {
    vm.description = value;
    internal.updateMetatags({
      name: 'description',
      itemprop: 'description',
      property: 'og:description',
    }, value);
  },
});

Object.defineProperty(meta, 'image', {
  get() { return vm.image; },
  set(value) {
    const imageUrl = `${window.location.origin}/${value}`;
    vm.image = imageUrl;
    internal.updateMetatags({
      itemprop: 'image',
      property: 'og:image',
    }, imageUrl);
  },
});

Object.defineProperty(meta, 'url', {
  get() { return vm.url; },
  set(value) {
    const url = `${window.location.origin}${value}`;
    vm.url = url;
    let ogUrl = select('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = create('meta');
      ogUrl.setAttribute('property', 'og:url');
      head.appendChild(ogUrl);
    }
    ogUrl.setAttribute('content', url);

    let canonical = select('link[rel="canonical"]');
    if (!canonical) {
      canonical = create('link');
      canonical.setAttribute('rel', 'canonical');
      head.appendChild(canonical);
    }
    canonical.href = url;

    const alternates = selectAll('link[rel="alternate"]');
    alternates.forEach(alternate => head.removeChild(alternate));
    LANGUAGES.forEach((lang) => {
      const link = create('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', lang);
      link.href = url.replace(`/${i18n.locale}/`, `/${lang}/`);
      head.appendChild(link);
    });
  },
});

export default meta;
